import * as React from 'react';
import { Component, Fragment } from 'react';
import { inject, observer } from 'mobx-react';
import { computed, action, isArrayLike } from 'mobx';
import styled, { withTheme } from 'styled-components';
import { propStyles } from '@kurtosys/ksys-portal-components/build/utils/propStyles';
import Icon from './Icon';
import DownloadControls from './DownloadControls';
import Translate from './Translate';
import WatchListToggle from './WatchListToggle';
import ChangeIndicator from './ChangeIndicator';
import DocumentMenu from './DocumentMenu';
import Tooltip from './Tooltip';
import { ShareclassCardList, ShareclassCardRow } from './ShareclassCards';
import ColumnDropdown from './ColumnDropdown';
import StarRating from './StarRating';
import DynamicLinkProps from '../models/components/DynamicLinkProps';
import DynamicLink from './DynamicLink';
import { CellDisclaimer } from '../models/configuration/explorer/CellDisclaimer';
import { DataStore } from '../stores';

const ShareclassCardLabel = styled<any, any>('div')`
    ${propStyles};
`;

ShareclassCardLabel.defaultProps = {
    fontFamily: 'heading',
    fontWeight: 'medium',
};

const ShareclassCardValue = styled<any, any>('div')`
    ${propStyles};
`;

ShareclassCardValue.defaultProps = {
    display: 'flex',
    alignItems: 'center',
    fontFamily: 'body',
    fontWeight: 'regular',
};

const starIconStyleProps = {
    height: '1.2em',
    width: '1.2em',
};

const SeparatorWrapper = styled<any, any>('span')`
    ${propStyles};
    white-space: pre;
`;

@inject('dataStore')
@observer
class ShareclassCardLabelValue extends Component<any> {
    constructor(props) {
        super(props);

        if (this.props.column.groups) {
            this.setInitialGroupIndex();
        }
    }

    loadDisclaimerSuperscripts = (column, columnContent) => {
        const { dataStore, shareClass: shareClassWrapper } = this.props;
        const entity = shareClassWrapper.shareClass;
        if (column.cellDisclaimers && column.cellDisclaimers.length > 0) {
            const cellDisclaimers = column.cellDisclaimers as Array<
                CellDisclaimer
            >;
            const disclaimerStore = (dataStore as DataStore).disclaimerStore;
            if (disclaimerStore) {
                const superscripts = disclaimerStore.getSuperscripts(
                    entity,
                    cellDisclaimers
                );
                if (superscripts && superscripts.length > 0) {
                    return (
                        <Fragment>
                            {columnContent}
                            <sup>{superscripts.join(', ')}</sup>
                        </Fragment>
                    );
                }
            }
        }
        return columnContent;
    };

    @action
    setInitialGroupIndex = () => {
        const selectedConfigGroupIndex = this.props.column.groups.findIndex(
            c => c.selected
        );
        const selectedGroupIndex = this.selectedGroupIndex
            ? this.selectedGroupIndex
            : selectedConfigGroupIndex > -1
            ? selectedConfigGroupIndex
            : 0;

        this.props.selectedGroupMap.set(
            this.props.columnIndex,
            selectedGroupIndex
        );
    };

    @computed
    get selectedGroupIndex() {
        let index = this.props.selectedGroupMap.get(this.props.columnIndex);
        if (this.props.column.groups) {
            if (index >= this.props.column.groups.length) {
                const selectedConfigGroupIndex = this.props.column.groups.findIndex(
                    c => c.selected
                );
                // tslint:disable-next-line: prefer-conditional-expression
                if (selectedConfigGroupIndex > -1) {
                    index = selectedConfigGroupIndex;
                } else {
                    index = 0;
                }
            }
        }
        return index;
    }

    @computed
    get column() {
        return this.props.column.groups
            ? this.props.column.groups[this.selectedGroupIndex]
            : this.props.column;
    }
    getGroupOrDropdownContent = (
        column,
        dynamicLinkProps: DynamicLinkProps
    ) => {
        const { theme, setSelectedGroup } = this.props;
        const { selectedGroupIndex } = this;
        return (
            <Fragment>
                {column.type === 'group' ? (
                    <ShareclassCardLabel {...theme.ShareclassCardGroupLabel}>
                        <Translate>{column.label}</Translate>
                        <Tooltip
                            content={column.disclaimers}
                            accessibilityContext={`for disclaimers of the ${column.label} column`}
                        />
                    </ShareclassCardLabel>
                ) : (
                    <ColumnDropdown
                        column={column}
                        selectedGroupIndex={selectedGroupIndex}
                        setSelectedGroup={setSelectedGroup}
                        theme={{
                            ...theme,
                            ColumnDropdownWrapper: {
                                ...theme.ColumnDropdownWrapper,
                                ...theme.ShareclassCardRowDropdownWrapper,
                            },
                            ColumnDropdownInnerWrapper: {
                                ...theme.ColumnDropdownInnerWrapper,
                                ...theme.ShareclassCardRowDropdownInnerWrapper,
                            },
                            ColumnDropdownGroupLabel: {
                                ...theme.ColumnDropdownGroupLabel,
                                ...theme.ShareclassCardRowDropdownGroupLabel,
                            },
                            ColumnDropdownChevron: {
                                ...theme.ColumnDropdownChevron,
                                ...theme.ShareclassCardRowDropdownChevron,
                            },
                            ColumnDropdownMenu: {
                                ...theme.ColumnDropdownMenu,
                                ...theme.ShareclassCardRowDropdownMenu,
                            },
                            ColumnDropdownList: {
                                ...theme.ColumnDropdownList,
                                ...theme.ShareclassCardRowDropdownList,
                            },
                            ColumnDropdownListItem: {
                                ...theme.ColumnDropdownListItem,
                                ...theme.ShareclassCardRowDropdownListItem,
                            },
                        }}
                    />
                )}
                <ShareclassCardList flex="1 0 100%" paddingX="0">
                    {column.values &&
                        column.values.map((c, cIndex) => (
                            <ShareclassCardRow
                                key={`${c.label}-${cIndex}`}
                                paddingY="sm"
                            >
                                <Fragment>
                                    <ShareclassCardLabel
                                        paddingX="md"
                                        {...theme.ShareclassCardLabel}
                                        {...theme.ShareclassCardLabelNested}
                                    >
                                        <Translate>{c.label}</Translate>
                                        <Tooltip content={c.disclaimers} />
                                    </ShareclassCardLabel>
                                    {this.getShareclassCardValue(
                                        c,
                                        dynamicLinkProps,
                                        { ...theme.ShareclassCardValueNested }
                                    )}
                                </Fragment>
                            </ShareclassCardRow>
                        ))}
                </ShareclassCardList>
            </Fragment>
        );
    };
    getDownloadContent = column => {
        const { theme, dataStore, shareClass, children, expanded } = this.props;
        let content = (
            <Fragment>
                <DownloadControls
                    count={shareClass.downloadCount}
                    theme={{
                        ...theme,
                        ...{
                            DownloadButton: theme.ShareclassCardDownloadButton,
                            DownloadButtonIcon:
                                theme.ShareclassCardDownloadButtonIcon,
                            DownloadCount: theme.ShareclassCardDownloadCount,
                        },
                    }}
                    onClick={e => {
                        e.stopPropagation();
                        dataStore.downloadDocuments(
                            shareClass.shareClassDownloads
                        );
                    }}
                />
                <Icon
                    fileName={
                        shareClass.expanded
                            ? 'DocumentDropdownIconExpanded'
                            : 'DocumentDropdownIcon'
                    }
                    height="1.4em"
                    width="1.4em"
                    {...theme.ShareclassCardDownloadIcon}
                />
            </Fragment>
        );
        content = this.loadDisclaimerSuperscripts(column, content);
        return (
            <Fragment>
                {this.getShareclassCardLabel(column)}
                <ShareclassCardValue>{content}</ShareclassCardValue>
                {expanded && children}
            </Fragment>
        );
    };
    getWatchlistContent = column => {
        const { shareClass } = this.props;
        return (
            <Fragment>
                {this.getShareclassCardLabel(column)}
                <WatchListToggle
                    onClick={e => {
                        e.stopPropagation();
                        shareClass.toggleWatchList();
                    }}
                    active={shareClass.inWatchList}
                    fontSize="1em"
                    accessibilityContext={`for share class identified by ${shareClass.shareClass.clientCode}`}
                />
            </Fragment>
        );
    };
    getDocumentContent = column => {
        const { theme, dataStore } = this.props;
        return (
            <Fragment>
                {Array.isArray(column.value) && column.value.length > 0 ? (
                    <DocumentMenu
                        documents={column.value}
                        showSingleDocument={column.showSingleDocument}
                        showSingleDocumentLabel={column.showSingleDocumentLabel}
                    >
                        {this.loadDisclaimerSuperscripts(
                            column,
                            this.getShareclassCardLabel(column)
                        )}
                    </DocumentMenu>
                ) : (
                    <Fragment>
                        {this.getShareclassCardLabel(column)}
                        <ShareclassCardValue {...theme.ShareclassCardValue}>
                            <Translate>
                                {column.noDataPlaceholder ||
                                    dataStore.config.noDataPlaceholder}
                            </Translate>
                        </ShareclassCardValue>
                    </Fragment>
                )}
            </Fragment>
        );
    };
    getPricesContent = column => {
        const { shareClass } = this.props;
        return (
            <Fragment>
                {this.getShareclassCardLabel(column)}
                {column.data.performanceCheck && !shareClass.hasPerformance ? (
                    <Translate>{column.data.value}</Translate>
                ) : (
                    <DownloadControls
                        display="inline-flex"
                        onClick={() => shareClass.getPriceHistory(column)}
                        count={1}
                        showCount={false}
                    />
                )}
            </Fragment>
        );
    };
    getStarsContent = column => {
        const { theme, shareClass } = this.props;
        if (column.data.performanceCheck && !shareClass.hasPerformance) {
            return <Translate>{column.data.value}</Translate>;
        }
        return (
            <Fragment>
                {this.getShareclassCardLabel(column)}
                {this.loadDisclaimerSuperscripts(
                    column,
                    <StarRating
                        rating={column.data.value}
                        noDataPlaceholder={column.noDataPlaceholder}
                        showEmptyStars={column.type === 'starsOutOf'}
                        display="block"
                        theme={{
                            ...theme,
                            ...theme.StarRatingColumnWrapper,
                            StarIcon: {
                                ...starIconStyleProps,
                                ...theme.StarIconColumn,
                            },
                            StarOutlineIcon: {
                                ...starIconStyleProps,
                                ...theme.StarOutlineIconColumn,
                            },
                        }}
                    />
                )}
            </Fragment>
        );
    };
    getSimpleContent = (column, dynamicLinkProps: DynamicLinkProps) => {
        return (
            <Fragment>
                {this.getShareclassCardLabel(column)}
                {this.getShareclassCardValue(column, dynamicLinkProps)}
            </Fragment>
        );
    };
    getShareclassCardLabel = (
        column,
        labelProps: any = {},
        themeKey = 'ShareclassCardLabel'
    ) => {
        const { theme } = this.props;
        return (
            <ShareclassCardLabel {...labelProps} {...theme[themeKey]}>
                <Translate>{column.label}</Translate>
                <Tooltip content={column.disclaimers} />
            </ShareclassCardLabel>
        );
    };
    getShareclassCardValue = (
        column,
        dynamicLinkProps: DynamicLinkProps,
        shareclassCardValueProps: any = {},
        themeKey: string = 'ShareclassCardValue'
    ) => {
        const { theme, dataStore } = this.props;
        const dataRecords = isArrayLike(column.data)
            ? column.data
            : [column.data];

        const values = [];
        dataRecords.forEach((dataRecord, index) => {
            let inner = (
                <Fragment>
                    <Translate>{dataRecord.value}</Translate>
                    {column.multiple && dataRecord.separator && (
                        <SeparatorWrapper>
                            {dataRecord.separator}
                        </SeparatorWrapper>
                    )}
                </Fragment>
            );
            if (dataRecord.showChange) {
                inner = (
                    <ChangeIndicator
                        dataStore={dataStore}
                        showChange={dataRecord.showChange}
                        value={dataRecord.value}
                        separator={dataRecord.separator}
                    />
                );
            }
            values.push(
                <Fragment key={`${dataRecord.code}-${index}`}>{inner}</Fragment>
            );
        });
        const linkContent = this.loadDisclaimerSuperscripts(column, values);
        return (
            <ShareclassCardValue
                {...theme[themeKey]}
                {...shareclassCardValueProps}
            >
                <DynamicLink {...dynamicLinkProps}>{linkContent}</DynamicLink>
            </ShareclassCardValue>
        );
    };
    getContent = column => {
        const { theme, dataStore, shareClass, queryContext } = this.props;
        const dynamicLinkProps: DynamicLinkProps = {
            dataStore,
            queryContext,
            config: column,
            shareClass: shareClass.shareClass,
            showLink: false,
            theme: theme.ShareclassColumnLink,
        };
        let content;
        if (['group', 'dropdown'].includes(column.type)) {
            dynamicLinkProps.showLink = true;
            content = this.getGroupOrDropdownContent(column, dynamicLinkProps);
        } else if (column.type === 'download') {
            content = this.getDownloadContent(column);
        } else if (column.type === 'watchlist' && dataStore.watchListEnabled) {
            content = this.getWatchlistContent(column);
        } else if (column.type === 'document') {
            content = this.getDocumentContent(column);
        } else if (column.type === 'prices') {
            content = this.getPricesContent(column);
        } else if (column.type === 'stars' || column.type === 'starsOutOf') {
            content = this.getStarsContent(column);
        } else {
            dynamicLinkProps.showLink = true;
            content = this.getSimpleContent(column, dynamicLinkProps);
        }

        return content;
    };

    render() {
        return this.getContent(this.column);
    }
}

export default withTheme(ShareclassCardLabelValue) as any;



// WEBPACK FOOTER //
// ./src/Components/ShareclassCardLabelValue.tsx